import { memo, useEffect, useState } from "react";
import {
  Card,
  Stack,
  Typography,
  IconButton,
  Collapse,
  useMediaQuery,
  useTheme,
  Button,
  Box,
  CircularProgress,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Menu,
  Checkbox,
  Badge,
} from "@material-ui/core";
import { grey } from "@material-ui/core/colors";
import moment from "moment-timezone";
import "moment/locale/he"; // without this line, language is not imported
import { ProcessedListingData, RealEstateContact } from "../../../@types/sharedSchema";
import { getListingMatchAdditionalDetails, numberToCurrency } from "utils/listings/display";
import { noteHealthRender } from "./listingsMatchHealth";
import {
  MoreVert,
  PersonSearch,
  Visibility,
  Favorite,
  FavoriteBorder,
  OpenInNew,
  Call,
  ImageNotSupported,
  Close,
  KeyboardArrowLeft,
  List,
  Delete,
  ContentCopy,
  Brush,
  NoteAlt,
  AddTask,
  Info,
  AutoFixHigh,
} from "@material-ui/icons";
import SvgIconStyle from "components/SvgIconStyle";
import { ListingPriceWithHistory } from "./ListingPriceWithHistory";
import Label from "components/Label";
import { useTranslation } from "react-i18next";
import { Grid } from "@material-ui/core";
import createAvatar from "utils/createAvatar";
import { MAvatar } from "components/@material-extend";
import RoomsFloorAndSqrMtr from "./RoomsFloorAndSqrMtr";
import {
  ListingBooleanPropertiesDetailsBag,
  ListingAdditionalDetailsComponent
} from "./ListingPropertiesAndAdditionalDetails";
import { CarouselCard } from "components/carousel/CarouselCard";
import { preventBubbling } from "utils/listings/helper";
import { useOpenListingDialog } from "../listings/modal/dialog/useOpenListingDialog";
import { useModal } from "hooks/useModal";
import { usePostHog } from "posthog-js/react";
import { ListingCardLikes } from "./ListingCardLikes";
import { ListingMatchAdTypeLabel } from "./ListingMatchAdTypeLabel";
import { parsePhoneNumber } from "utils/Haim/phoneNumbersUtils";
import { getEpochTimeFromDaysAgo } from "utils/listingsMatch/data";
import { ListingPicturesVisibilityDialog } from "./ListingPicturesVisibilty";
import { ListingMatchDescriptionOverride } from "./ListingMatchDescriptionOverride";
import { ModalAsyncResult } from "components/__haim/Common/ConfirmationDialog";
import { ListingCardNotes } from "./ListingCardNotes";
import { showModalAsync } from "components/__haim/Common/ConfirmationDialog";
import { useModal as useMuiModal } from "mui-modal-provider";
import { MobileBranding, DesktopBranding } from "./Branding";
import { ListingCardVisits } from "./ListingCardVisits";
import { ListingOrgActivityIndicator } from "../listings/orgActivity/ListingOrgActivityIndicator";
import { NewListingDialog } from "../listings/modal/dialog/NewListingDialog";
import { convertProcessedListingDataToListing } from "utils/listingsMatch/parser";
import { useLinkListingToMarketAd } from "hooks/useLinkListingToMarketAd";
import { resolveListingSeller } from "utils/listings/listingSeller";
import CreativesGeneratorModal from "../creatives-generator/CreativesGeneratorModal";
import { WatermarkRemovalDialog } from "../watermarks/WatermarkRemovalDialog";
import {
  buildWatermarkFreeUrlMap,
} from "utils/listings/watermarkFree";
import useAuth from "hooks/useAuth";
import useFirestore from "hooks/useFirestore";
import useCalendar from "hooks/useCalendar";
import { useListingEvents } from "hooks/useListingEvents";
import { CalendarEventDialog } from "components/__haim/Common/CalendarEventDialog";
import type { CalendarEvent, FavoriteListingsOfContact } from "../../../@types/sharedSchema";
import type { AlgoliaHit } from "instantsearch.js";
import type { MouseEvent } from "react";

export interface ListingMatchRowProps {
  listing: AlgoliaHit<ProcessedListingData>;
  isFavorite?: boolean;
  isSelected?: boolean;
  imagesHidden?: boolean;
  isSelectable?: boolean;
  isInListsTab?: boolean;
  isInBlacklist?: boolean;
  pickedContact?: RealEstateContact;
  isInEventDialog?: boolean;
  showFavoriteButton?: boolean;
  inListingMatchToContacts?: boolean;
  favoriteListingsOfContact?: FavoriteListingsOfContact;
  onAddToFavorite?: (hit: AlgoliaHit<ProcessedListingData>) => void;
  onAddToBlacklist?: (hit: AlgoliaHit<ProcessedListingData>) => void;
  onRemoveFromFavorite?: (hit: AlgoliaHit<ProcessedListingData>) => void;
  onRemoveFromBlacklist?: (hit: AlgoliaHit<ProcessedListingData>) => void;
  onToggleListingSelection?: (listingId: string, isSelected: boolean) => void;
  onAddToListingsList?: (hit: AlgoliaHit<ProcessedListingData>) => void;
  onRemoveFromListingsList?: (listingId: string) => void;
}

/**
 * The seller block — avatar plus the identity column — as the desktop row and
 * the mobile card both render it.
 *
 * ## Why it is one component and not two copies
 *
 * It was duplicated verbatim, and the copies had already begun to drift (one
 * wraps the phone number, the other does not). That is the same shape of bug the
 * seller RESOLUTION above exists to end — one thing shown two different ways —
 * so the fix belongs on both halves, not just the data one.
 *
 * ## And why the extraction is load-bearing, not tidying
 *
 * `eslint-plugin-react-hooks` is pinned at 4.2.0 here, where `rules-of-hooks`
 * decides "was this hook called unconditionally?" by multiplying code-path counts
 * as plain JS numbers (the BigInt fix landed in a later release). Past a size
 * threshold that product loses precision, the comparison fails, and EVERY hook
 * below it in the component is reported as "called conditionally" — 13 errors
 * here, all of them false. Nothing was ever conditional; this component had
 * simply grown past the rule's arithmetic, and adding the role caption's two
 * branches tipped it over.
 *
 * Ten conditional branches move out of the row with this block, which is what
 * puts it back under the threshold. Keep new markup out of the row's own JSX for
 * the same reason: the next few branches will bring the phantom errors back, and
 * they will point at hooks that have nothing to do with the change.
 */
function ListingSellerDetails({
  name,
  agencyName,
  phone,
  roleLabel,
  nowrapPhone
}: {
  name?: string;
  agencyName?: string;
  phone?: string;
  /** בעל הנכס / מפרסם — never a fixed string; see `resolveListingSeller`. */
  roleLabel?: string | null;
  nowrapPhone?: boolean;
}): JSX.Element {
  // "N/A" is what the scrapers write when a marketplace published no seller at
  // all, so it is an absence wearing a name.
  const hasName = !!name && name !== "N/A";
  const { name: avatarName, color: avatarColor } = createAvatar(name);

  return (
    <>
      {hasName && (
        <MAvatar sx={{ width: 36, height: 36 }} color={avatarColor}>
          {avatarName}
        </MAvatar>
      )}
      <Stack direction="column" alignItems="flex-start">
        {roleLabel && (
          <Typography variant="caption" sx={{ color: grey[600] }}>
            {roleLabel}
          </Typography>
        )}
        {hasName && (
          <Typography variant="body2" style={{ fontWeight: "bold" }}>
            {name}
          </Typography>
        )}
        {agencyName && <Typography variant="body2">{agencyName}</Typography>}
        {phone && (
          <Typography
            sx={{ color: grey[800] }}
            variant="caption"
            whiteSpace={nowrapPhone ? "nowrap" : undefined}
          >
            {parsePhoneNumber(phone)?.formatNational() ?? phone}
          </Typography>
        )}
      </Stack>
    </>
  );
}

const ListingsMatchRow = memo(
  ({
    listing,
    isFavorite,
    isSelected,
    imagesHidden,
    isInListsTab,
    isSelectable,
    isInBlacklist,
    pickedContact,
    isInEventDialog,
    showFavoriteButton,
    inListingMatchToContacts,
    favoriteListingsOfContact,
    onAddToFavorite,
    onAddToBlacklist,
    onRemoveFromFavorite,
    onRemoveFromBlacklist,
    onToggleListingSelection,
    onAddToListingsList,
    onRemoveFromListingsList
  }: ListingMatchRowProps): JSX.Element => {
    noteHealthRender("row");
    const { t: translate } = useTranslation();

    const { createOrUpdateEvent, deleteEvent, getInitialEventByType } = useCalendar();
    const { userSettings } = useFirestore();
    const linkCopyToMarketAd = useLinkListingToMarketAd();

    const listingEvents = useListingEvents(listing.id);

    const theme = useTheme();
    const posthog = usePostHog();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const {
      address,
      addressBeforeNormalization,
      price,
      adType,
      roomsCount,
      squareMeter,
      originalListingUrl,
      updatedTime,
      listingUpdatedTime,
      listingPublishedTime,
      createdTime,
      description,
      // `sellerPhoneNumber` / `sellerName` / `agencyName` are deliberately NOT
      // destructured here: they mean different people on different rows, and the
      // only correct way to read them is `resolveListingSeller` below.
      tags,
      imageUrls,
      listingOrigin,
      propertyCondition,
      sizeOfPlotM2,
      propertyType,
      priceHistory
    } = listing;

    const { street, city, neighborhood, floor, houseNumber } =
      addressBeforeNormalization ?? address ?? {};

    /**
     * WHO the seller block describes.
     *
     * `sellerName` / `sellerPhoneNumber` / `agencyName` must never be rendered
     * as "the seller" straight off the row: on a `webot` one the mirror Cloud
     * Function overwrites all three with the OWNING REALTOR's own name, phone
     * and company, so there they are the LISTING AGENT. One resolver, every
     * surface — see `utils/listings/listingSeller.ts`.
     */
    const seller = resolveListingSeller(listing);
    const listingSellerPhoneNumber = seller.phone;
    const whatsappText = `היי, בקשר למודעה הזאת: ${originalListingUrl}`;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _whatsappDirectUrlToSeller = `https://wa.me/${listingSellerPhoneNumber}?text=${encodeURIComponent(
      whatsappText
    )}`;

    const listingCreatedTime = listingPublishedTime ?? createdTime;
    const createdTimeMomentAgo = moment.parseZone(listingCreatedTime)?.locale("he").fromNow();
    const refreshedTimeMomentAgo = moment.parseZone(updatedTime)?.locale("he").fromNow();
    const listingUpdatedTimeMomentAgo = listingUpdatedTime
      ? moment.parseZone(listingUpdatedTime)?.locale("he").fromNow()
      : refreshedTimeMomentAgo;

    const isListingFromWebot = listingOrigin === "webot";
    const isListingFromYad2 = listingOrigin === "yad2";
    const isListingFromMadlan = listingOrigin === "madlan";

    const showListingActions = !isInEventDialog;

    // Potentially old if the webot listing hasn't been updated in more than 1 year
    const lastWebotUpdatedTime = listingUpdatedTime ?? updatedTime;
    const isListingPotentiallyOldFromWebot =
      isListingFromWebot && lastWebotUpdatedTime?.getTime?.() < getEpochTimeFromDaysAgo(365);

    // Potentially removed if the updated time is more than 7 days ago (only for listing that is from yad2)
    const isListingPotentiallyRemovedFromYad2 =
      isListingFromYad2 && updatedTime?.getTime?.() < getEpochTimeFromDaysAgo(7);

    // Potentially old if the updated time is more than 3 days ago (only for listing that is from madlan)
    const isListingPotentiallyOldFromMadlan =
      isListingFromMadlan && updatedTime?.getTime?.() < getEpochTimeFromDaysAgo(3);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const [expanded, setExpanded] = useState(false);
    const [favorited, setFavorited] = useState(false);
    const [imagesBlurred, setImagesBlurred] = useState(false);

    const openListingDialog = useOpenListingDialog();

    const { showModal: showDeleteConfirmationModal } = useMuiModal();

    const {
      open: listingPictureVisibilityDialogOpen,
      openModal: openListingPictureVisibilityDialog,
      closeModal: closeListingPictureVisibilityDialog
    } = useModal();

    const {
      open: newListingDialogOpen,
      openModal: openNewListingDialog,
      closeModal: closeNewListingDialog
    } = useModal();

    const {
      open: creativesGeneratorModalOpen,
      openModal: openCreativesGeneratorModal,
      closeModal: closeCreativesGeneratorModal
    } = useModal();

    const {
      open: newTaskEventOpen,
      openModal: openNewTaskEventModal,
      closeModal: closeNewTaskEventModal
    } = useModal();

    const {
      open: watermarkRemovalDialogOpen,
      openModal: openWatermarkRemovalDialog,
      closeModal: closeWatermarkRemovalDialog
    } = useModal();

    useEffect(() => {
      setFavorited(isFavorite);
    }, [isFavorite]);

    useEffect(() => {
      setImagesBlurred(imagesHidden);
    }, [imagesHidden]);

    const handleExpandClick = () => {
      setExpanded(!expanded);
    };

    const handleFavoriteClick = (event: React.MouseEvent) => {
      console.log("favorited", favorited);
      if (favorited) {
        console.log("removeListingFromContactsFavorites", listing.objectID);
        onRemoveFromFavorite(listing);

        // Early update the UI
        setImagesBlurred(false);
      } else {
        console.log("addListingToContactsFavorites", listing.objectID);
        onAddToFavorite(listing);
        posthog?.capture("added_listing_to_minisite", {
          listing: listing.id,
          listingOrigin: listingOrigin,
          fromSource: "listing_match_row"
        });
        if (isListingFromMadlan) {
          posthog?.capture("madlan_added_listing_to_minisite", {
            listing: listing.id,
            fromSource: "listing_match_row"
          });
        }
      }

      // Early update the UI
      setFavorited(!favorited);
    };

    const handleMenuOpen = (event: MouseEvent<HTMLButtonElement>): void => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = (): void => {
      setAnchorEl(null);
    };

    const handleAddToBlacklistClick = (event: React.MouseEvent) => {
      event.stopPropagation();
      onAddToBlacklist?.(listing);
    };

    const handleRemoveFromBlacklistClick = (event: React.MouseEvent) => {
      event.stopPropagation();
      onRemoveFromBlacklist?.(listing);
    };

    const handleOriginalListingClick = (event: React.MouseEvent) => {
      event.stopPropagation();
      window.open(originalListingUrl, "_blank");
    };

    const handleFindBuyers = (): void => {
      handleMenuClose();
      void openListingDialog({ algoliaListing: listing, initialTab: "buyers" });
    };

    const handleOpenMoreDetails = (): void => {
      handleMenuClose();
      void openListingDialog({ algoliaListing: listing });
    };

    const handleAddToList = (): void => {
      handleMenuClose();
      onAddToListingsList(listing);
    };

    const handleAddListingToWebot = (): void => {
      handleMenuClose();
      openNewListingDialog();
    };

    const handleCreateCreative = (): void => {
      handleMenuClose();
      openCreativesGeneratorModal();
    };

    const handleRemoveWatermarks = (): void => {
      handleMenuClose();
      openWatermarkRemovalDialog();
    };

    const showDeleteConfirmation = (): Promise<ModalAsyncResult> =>
      showModalAsync({
        showModalInstance: showDeleteConfirmationModal,
        title: translate("listingsMatch.listManagement.deleteListingFromListTitle"),
        description: translate("listingsMatch.listManagement.deleteListingFromListDescription"),
        showConfirm: true,
        showCancel: true
      });

    const handleRemoveFromList = async (): Promise<void> => {
      const result = await showDeleteConfirmation();

      if (result !== ModalAsyncResult.confirm) return;

      handleMenuClose();
      onRemoveFromListingsList(listing.objectID);
    };

    const onCheckboxToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      onToggleListingSelection(listing.objectID, event.target.checked);
    };

    const getInitialEventData = (): CalendarEvent => {
      const parsedListingIds = [listing.id];
      const parsedListingsData = [listing];

      return getInitialEventByType("task", {
        listingIds: parsedListingIds,
        listingsData: parsedListingsData
      });
    };

    const cityAndNeighborhoodComponent = (
      <Stack direction="row">
        {!!neighborhood && <Typography variant="body2">{neighborhood}</Typography>}
        {!!city && !!neighborhood && <Typography variant="body2">{","}</Typography>}
        {!!city && (
          <Typography sx={{ pl: 0.5 }} variant="body2">
            {city}
          </Typography>
        )}
      </Stack>
    );

    // Mobile and desktop source icon components using shared logic
    const mobileSourceIcon = <MobileBranding listingOrigin={listingOrigin} />;

    const desktopSourceIcon = <DesktopBranding listingOrigin={listingOrigin} />;

    const listingTitleComponent = (
      <Stack direction="row" alignItems="center" spacing={1}>
        {isMobile && mobileSourceIcon}
        <Typography variant="h5">
          {!street ? cityAndNeighborhoodComponent : `${street} ${houseNumber ?? ""}`}
        </Typography>
      </Stack>
    );

    const listingSubtitleComponent = (
      <Typography variant="body2">{!street ? "" : cityAndNeighborhoodComponent}</Typography>
    );

    const listingLikesAndVisitsVisible =
      pickedContact && showFavoriteButton && favoriteListingsOfContact;

    const listingLikesVisibleComponent = listingLikesAndVisitsVisible && (
      <>
        <ListingCardLikes
          small
          listingId={listing.objectID}
          favoriteListingsOfContact={favoriteListingsOfContact}
        />
        <ListingCardVisits
          listingId={listing.objectID}
          favoriteListingsOfContact={favoriteListingsOfContact}
        />
      </>
    );

    const listingNotesLikesVisitsVisible = favoriteListingsOfContact;

    const listingNotesLikesVisitsComponent = listingNotesLikesVisitsVisible && (
      <Stack
        sx={{ my: 1 }}
        gap={1}
        direction={{
          xs: "column",
          sm: "row"
        }}
        alignItems="center"
      >
        <ListingCardNotes
          small
          listingId={listing.objectID}
          contactData={pickedContact}
          favoriteListingsOfContact={favoriteListingsOfContact}
        />
        {listingLikesVisibleComponent}
      </Stack>
    );

    const listingDescriptionOverrideComponent = (
      <ListingMatchDescriptionOverride
        favorited={favorited}
        listingId={listing.objectID}
        description={description}
        favoriteListingsOfContact={favoriteListingsOfContact}
      />
    );

    const opacity = isInBlacklist ? 0.3 : 1;

    const imageSize = isMobile
      ? {
          width: 176,
          height: 144
        }
      : {
          width: 208,
          height: 176
        };

    const parsedImages =
      imageUrls?.length > 0
        ? imageUrls?.map((url, index) => ({
            id: index.toString(),
            src: url,
            alt: url
          }))
        : null;

    const sellerDisplayName = seller.name;
    const sellerAgencyName = seller.agencyName;
    // Is there anything to show at all? The block itself decides what to show —
    // see `ListingSellerDetails`, which also owns the avatar and the "N/A" rule.
    const hasAnySellerDetail =
      (!!sellerDisplayName && sellerDisplayName !== "N/A") ||
      !!listingSellerPhoneNumber ||
      !!sellerAgencyName;
    // A `webot` row that records no owner knows only the LISTING AGENT. Saying
    // whose details these are costs one caption; not saying it is how a realtor
    // reads her own number as the seller's.
    const sellerRoleLabel =
      seller.kind === "owner"
        ? translate("listingsBoard.seller.owner")
        : seller.kind === "agent"
        ? translate("listingsBoard.seller.agent")
        : null;

    const { hiddenPictures } = getListingMatchAdditionalDetails({
      listingId: listing.objectID,
      favoriteListingsOfContact: favoriteListingsOfContact
    });

    // Clean (watermark-free) versions display for any listing that has them, once the realtor has
    // accepted the rights terms — there is no per-listing gate any more. The carousel mirrors what
    // their minisites render. DISPLAY copies only: the visibility dialog below keeps receiving the
    // ORIGINAL urls because hidden-picture lists are keyed by them, so the hidden list is
    // translated through the same original→clean map.
    const cleanBySource = userSettings?.watermarkTermsAcceptedAt
      ? buildWatermarkFreeUrlMap(listing)
      : new Map<string, string>();
    const displayImages = parsedImages
      ? parsedImages.map((image) => ({
          ...image,
          src: cleanBySource.get(image.src) ?? image.src
        }))
      : null;
    const displayHiddenPictures = hiddenPictures?.map(
      (url: string) => cleanBySource.get(url) ?? url
    );

    const parsedEvent = getInitialEventData();
    const parsedListing = convertProcessedListingDataToListing(listing);

    return (
      <>
        {/* Same action as the listings board's "add to Webot", and now the same
            follow-up: record that this copy came from that market ad. Without
            the link the server never suppresses the original, so the realtor is
            left with a permanent visible duplicate — the ad and her copy, side
            by side, forever. That the two surfaces diverged at all was the bug:
            one identical button, two different outcomes. */}
        <NewListingDialog
          duplicating
          open={newListingDialogOpen}
          listing={parsedListing}
          onClose={closeNewListingDialog}
          onConfirm={handleMenuClose}
          onListingCreation={(newListingId) => {
            void linkCopyToMarketAd(newListingId, listing.objectID ?? listing.id);
            closeNewListingDialog();
          }}
        />
        <CalendarEventDialog
          open={newTaskEventOpen}
          event={parsedEvent}
          onClose={closeNewTaskEventModal}
          deleteEvent={deleteEvent}
          createOrUpdateEvent={createOrUpdateEvent}
        />
        <ListingPicturesVisibilityDialog
          open={listingPictureVisibilityDialogOpen}
          pictures={parsedImages}
          listingId={listing.objectID}
          favoriteListingOfContact={favoriteListingsOfContact}
          onClose={closeListingPictureVisibilityDialog}
        />
        <CreativesGeneratorModal
          open={creativesGeneratorModalOpen}
          onClose={closeCreativesGeneratorModal}
          listingId={listing.objectID}
        />
        <WatermarkRemovalDialog
          open={watermarkRemovalDialogOpen}
          listingId={listing.objectID}
          onClose={closeWatermarkRemovalDialog}
        />
        <Card
          variant="outlined"
          sx={{ pb: 1 }}
          style={{ cursor: "pointer", opacity: opacity }}
          key={listing.objectID}
          onClick={preventBubbling(handleExpandClick)}
        >
          {/* <div>{`#${listing.id}`}</div> */}
          {(isListingPotentiallyRemovedFromYad2 || isListingPotentiallyOldFromMadlan) && (
            <Label
              color="error"
              style={{
                position: "relative"
              }}
              sx={{ p: 1 }}
            >
              {translate("listingsMatch.listingIsPotentiallyRemoved")}
            </Label>
          )}
          {isListingPotentiallyOldFromWebot && (
            <Label
              color="warning"
              style={{
                position: "relative"
              }}
              sx={{ p: 1 }}
            >
              {translate("listingsMatch.listingIsPotentiallyOld")}
            </Label>
          )}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack sx={{ px: 1 }} direction="row" alignItems="center" gap={0.5}>
              {isSelectable && (
                <Checkbox
                  checked={isSelected}
                  onChange={onCheckboxToggle}
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                  color="secondary"
                />
              )}
              {!inListingMatchToContacts && (
                <IconButton size="small" onClick={handleMenuOpen}>
                  <MoreVert />
                </IconButton>
              )}
              <Menu
                anchorEl={anchorEl}
                open={!!anchorEl}
                anchorOrigin={{
                  vertical: 37,
                  horizontal: "left"
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left"
                }}
                onClose={preventBubbling(handleMenuClose)}
              >
                <MenuItem onClick={preventBubbling(handleOpenMoreDetails)}>
                  <ListItemIcon>
                    <Info />
                  </ListItemIcon>
                  <ListItemText>
                    {translate("listingsMatch.control.moreDetails", "פרטים נוספים")}
                  </ListItemText>
                </MenuItem>
                {/* Inside a task/meeting the card is a reference to the listing, so
                    its menu offers only "פרטים נוספים" — the rest of the actions
                    live on the listing dialog that one opens. */}
                {showListingActions && listing.listingListedFor !== "rent" && (
                  <MenuItem onClick={preventBubbling(handleFindBuyers)}>
                    <ListItemIcon>
                      <PersonSearch />
                    </ListItemIcon>
                    <ListItemText>{translate("listingsMatch.control.findBuyers")}</ListItemText>
                  </MenuItem>
                )}
                {showListingActions && (
                  <MenuItem onClick={preventBubbling(handleCreateCreative)}>
                    <ListItemIcon>
                      <Brush />
                    </ListItemIcon>
                    <ListItemText>
                      {translate("creativesGenerator.createNew", "יצירת קריאטיב")}
                    </ListItemText>
                  </MenuItem>
                )}
                {showListingActions && (
                  <MenuItem onClick={preventBubbling(handleRemoveWatermarks)}>
                    <ListItemIcon>
                      <AutoFixHigh />
                    </ListItemIcon>
                    <ListItemText>
                      {translate("watermarkRemoval.dialogTitle", "הסרת סימני מים")}
                    </ListItemText>
                  </MenuItem>
                )}
                {showListingActions && !isListingFromWebot && (
                  <MenuItem onClick={preventBubbling(handleAddListingToWebot)}>
                    <ListItemIcon>
                      <ContentCopy />
                    </ListItemIcon>
                    {/* One label for one action. It read "ייבוא ל-Webot" here,
                        "הוסף ל-Webot" on the board and "ייבוא נכס ל-Webot" on
                        rental-to-sale, for the same thing. */}
                    <ListItemText>{translate("listingsBoard.menu.addToWebot")}</ListItemText>
                  </MenuItem>
                )}
                {onAddToListingsList && (
                  <MenuItem onClick={preventBubbling(handleAddToList)}>
                    <ListItemIcon>
                      <List />
                    </ListItemIcon>
                    <ListItemText>{translate("listingsMatch.addSelectedToList")}</ListItemText>
                  </MenuItem>
                )}
                {isInListsTab && onRemoveFromListingsList && (
                  <MenuItem onClick={preventBubbling(handleRemoveFromList)}>
                    <ListItemIcon>
                      <Delete />
                    </ListItemIcon>
                    <ListItemText>{translate("listingsMatch.removeFromList")}</ListItemText>
                  </MenuItem>
                )}
                {showListingActions && listing.id && (
                  <MenuItem onClick={preventBubbling(openNewTaskEventModal)}>
                    <ListItemIcon>
                      <AddTask />
                    </ListItemIcon>
                    <ListItemText>{translate("listingsMatch.addTaskOrMeeting")}</ListItemText>
                  </MenuItem>
                )}
              </Menu>
              {listingEvents.isLoading ? (
                <CircularProgress sx={{ ml: -1, mr: 1 }} size={16} />
              ) : (
                !!listingEvents.events.length && (
                  <Badge
                    sx={{ ml: -1, mr: 1, top: 4 }}
                    color="primary"
                    badgeContent={listingEvents.events.length}
                  >
                    <NoteAlt color="action" />
                  </Badge>
                )
              )}
              <ListingMatchAdTypeLabel adType={adType} />
              <Typography variant={"caption"} style={{ color: "GrayText" }}>
                {`${translate("listingsMatch.createdTime")} ${createdTimeMomentAgo}`}
              </Typography>
              {!isMobile && (
                <Stack direction="row" alignItems="baseline" spacing={1} sx={{ pl: 3 }}>
                  {listingTitleComponent}
                  {listingSubtitleComponent}
                </Stack>
              )}
            </Stack>
            <Stack direction="row" alignItems="center">
              {!isMobile && listingNotesLikesVisitsComponent}
              {!isInListsTab && onAddToBlacklist && onRemoveFromBlacklist && (
                <Button
                  variant="text"
                  title={
                    isInBlacklist
                      ? translate("listingsMatch.removeFromBlacklist")
                      : translate("listingsMatch.addToBlacklist")
                  }
                  color="primary"
                  onClick={
                    isInBlacklist ? handleRemoveFromBlacklistClick : handleAddToBlacklistClick
                  }
                >
                  {isInBlacklist
                    ? translate("listingsMatch.removeFromBlacklist")
                    : translate("listingsMatch.addToBlacklist")}
                </Button>
              )}
              {isInListsTab && onRemoveFromListingsList && (
                <Button
                  variant="text"
                  title={translate("listingsMatch.removeFromList")}
                  color="error"
                  onClick={preventBubbling(handleRemoveFromList)}
                  startIcon={<Delete />}
                >
                  {translate("listingsMatch.removeFromList")}
                </Button>
              )}
              {/* A webot row has no external ad: its `originalListingUrl` is empty,
                  and opening it gave the realtor an about:blank tab. Same rule as
                  the listings board and rental-to-sale rows. */}
              {originalListingUrl && !isListingFromWebot && (
                <IconButton onClick={handleOriginalListingClick}>
                  <OpenInNew color="primary" />
                </IconButton>
              )}
              <IconButton
                href={listingSellerPhoneNumber ? `tel:${listingSellerPhoneNumber}` : undefined}
                disabled={!listingSellerPhoneNumber}
                onClick={(e) => e.stopPropagation()}
              >
                <Call color={listingSellerPhoneNumber ? "primary" : "disabled"} />
              </IconButton>
            </Stack>
          </Stack>
          <Grid
            container
            gap={1}
            direction="row"
            alignItems="center"
            justifyContent="left"
            sx={{ px: 2 }}
          >
            <Grid item xs={12}>
              <Stack
                sx={
                  isInEventDialog
                    ? {
                        display: {
                          xs: "grid",
                          md: "flex"
                        }
                      }
                    : undefined
                }
                direction="row"
                gap={2}
              >
                <Box
                  sx={{
                    height: imageSize.height,
                    overflow: "hidden",
                    position: "relative",
                    borderRadius: 1,
                    ...(isMobile ? { width: "100%" } : { width: imageSize.width, flexShrink: 0 })
                  }}
                >
                  {showFavoriteButton && (
                    <Stack
                      sx={{
                        p: 1,
                        top: 0,
                        width: "100%",
                        zIndex: 1,
                        position: "absolute"
                      }}
                      direction="row"
                      justifyContent="space-between"
                    >
                      {typeof isFavorite !== "boolean" ? (
                        <CircularProgress />
                      ) : (
                        <>
                          <IconButton
                            sx={{
                              left: 0,
                              border: 1,
                              bgcolor: "rgba(0, 0, 0, 0.4)",
                              borderColor: "divider",
                              "&:hover": {
                                bgcolor: "rgba(0, 0, 0, 0.6)"
                              }
                            }}
                            onClick={preventBubbling(handleFavoriteClick)}
                          >
                            {favorited ? (
                              <Favorite color="error" />
                            ) : (
                              <FavoriteBorder sx={{ color: "white" }} />
                            )}
                          </IconButton>
                          {favorited && (
                            <IconButton
                              sx={{
                                color: "white",
                                border: 1,
                                bgcolor: "rgba(0, 0, 0, 0.4)",
                                borderColor: "divider",
                                "&:hover": {
                                  bgcolor: "rgba(0, 0, 0, 0.6)"
                                }
                              }}
                              onClick={preventBubbling(openListingPictureVisibilityDialog)}
                            >
                              <Visibility />
                            </IconButton>
                          )}
                        </>
                      )}
                    </Stack>
                  )}
                  {displayImages ? (
                    <CarouselCard
                      height={imageSize.height}
                      pictures={displayImages}
                      listingMatch
                      cropTopImage
                      imagesHidden={imagesBlurred}
                      arrowIconSize="small"
                      hiddenPictures={displayHiddenPictures}
                    />
                  ) : (
                    <Stack
                      style={{
                        opacity: imagesBlurred ? 0.5 : 1,
                        transition: "opacity 300ms"
                      }}
                      alignItems="center"
                    >
                      <ImageNotSupported
                        color="disabled"
                        sx={{
                          width: isMobile ? imageSize.width : imageSize.width,
                          height: imageSize.height
                        }}
                      />
                    </Stack>
                  )}
                </Box>
                {isMobile ? (
                  <RoomsFloorAndSqrMtr
                    verticalDisplay
                    floor={floor}
                    roomsCount={roomsCount}
                    areaSizeM2={squareMeter}
                    plotSizeM2={sizeOfPlotM2}
                  >
                    {listingNotesLikesVisitsComponent}
                  </RoomsFloorAndSqrMtr>
                ) : (
                  <Stack sx={{ width: "100%" }} gap={1}>
                    <Stack justifyContent="space-between" direction="row">
                      <Stack gap={1}>
                        <Stack direction="row" gap={1} alignItems="center">
                          <ListingPriceWithHistory price={price} priceHistory={priceHistory} variant="body1" />
                          <RoomsFloorAndSqrMtr
                            floor={floor}
                            roomsCount={roomsCount}
                            areaSizeM2={squareMeter}
                            plotSizeM2={sizeOfPlotM2}
                          />
                        </Stack>
                        <Stack direction="row" gap={1}>
                          {propertyType && <Label color="primary">{propertyType}</Label>}
                          {propertyCondition && <Label color="info">{propertyCondition}</Label>}
                          {tags?.map((tag) => (
                            <Label color="warning">{tag}</Label>
                          ))}
                          {listing.isAssetExclusive && (
                            <Label color="error">
                              <SvgIconStyle
                                sx={{ width: 16, height: 16 }}
                                src="/static/icons/diamond.svg"
                              />
                              {translate("listingsPage.filter.isAssetExclusive")}
                            </Label>
                          )}
                        </Stack>
                      </Stack>
                      {hasAnySellerDetail && (
                        <Stack direction="row" alignItems="center" gap={1}>
                          <ListingSellerDetails
                            nowrapPhone
                            name={sellerDisplayName}
                            agencyName={sellerAgencyName}
                            phone={listingSellerPhoneNumber}
                            roleLabel={sellerRoleLabel}
                          />
                        </Stack>
                      )}
                    </Stack>
                    {listingDescriptionOverrideComponent}
                  </Stack>
                )}
              </Stack>
            </Grid>
            {isMobile && (
              <>
                <Grid item>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Stack direction="column">
                      {listingTitleComponent}
                      {listingSubtitleComponent}
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        <ListingPriceWithHistory price={price} priceHistory={priceHistory} variant="body1" />
                      </Stack>
                    </Stack>
                  </Stack>
                </Grid>
                {hasAnySellerDetail && (
                  <Grid item style={{ flexGrow: 1 }}>
                    <Stack
                      direction="row"
                      alignItems={"center"}
                      justifyContent={isMobile ? "flex-start" : "flex-end"}
                      spacing={1}
                    >
                      <ListingSellerDetails
                        name={sellerDisplayName}
                        agencyName={sellerAgencyName}
                        phone={listingSellerPhoneNumber}
                        roleLabel={sellerRoleLabel}
                      />
                    </Stack>
                  </Grid>
                )}
              </>
            )}
          </Grid>
          {isMobile && (
            <>
              <Grid container sx={{ p: 1 }}>
                {propertyType && (
                  <Grid item sx={{ px: 1 }}>
                    <Label color="primary">{propertyType}</Label>
                  </Grid>
                )}
                {propertyCondition && (
                  <Grid item sx={{ px: 1 }}>
                    <Label color="info">{propertyCondition}</Label>
                  </Grid>
                )}
                {listing.isAssetExclusive && (
                  <Grid item sx={{ px: 1 }}>
                    <Label color="error">
                      <SvgIconStyle
                        sx={{ width: 16, height: 16 }}
                        src="/static/icons/diamond.svg"
                      />
                      {translate("listingsPage.filter.isAssetExclusive")}
                    </Label>
                  </Grid>
                )}
                {tags &&
                  tags.map((tag) => (
                    <Grid item key={tag} sx={{ px: 1 }}>
                      <Label color="warning">{tag}</Label>
                    </Grid>
                  ))}
              </Grid>
              <Grid container sx={{ px: 2, pt: 1, pb: 1 }}>
                <Grid item xs={12}>
                  {listingDescriptionOverrideComponent}
                </Grid>
              </Grid>
            </>
          )}
          <Box sx={{ px: 2, mt: isMobile ? undefined : 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Button
                variant="text"
                size="small"
                onClick={handleExpandClick}
                endIcon={expanded ? <Close /> : <KeyboardArrowLeft />}
              >
                {expanded
                  ? translate("listingsMatch.lessDetails")
                  : translate("listingsMatch.moreDetails")}
              </Button>
              {/* Org-mates' notes/meetings/tasks on this listing, then the source
                  logo in the corner. In the flow, side by side: the logo used to be
                  absolutely positioned into this same corner and covered the
                  org-mates' avatars. */}
              <Stack direction="row" alignItems="center" gap={1}>
                <ListingOrgActivityIndicator listingId={listing.objectID} />
                {!isMobile && desktopSourceIcon}
              </Stack>
            </Stack>
            <Collapse in={expanded} unmountOnExit>
              <Stack direction="column" gap={2}>
                <ListingAdditionalDetailsComponent showAddressDisplayMode listing={listing} />
                <Stack direction={"column"} spacing={1} alignItems="flex-start" sx={{ pt: 1 }}>
                  <Label color="info">
                    {`${translate("listingsMatch.createdTime")} ${createdTimeMomentAgo}`}
                  </Label>
                  <Label color="info">
                    {`${translate("listingsMatch.updated")} ${listingUpdatedTimeMomentAgo}`}
                  </Label>
                  <Label color="info">
                    {`${translate("listingsMatch.refreshedTime")} ${refreshedTimeMomentAgo}`}
                  </Label>
                </Stack>
                <ListingBooleanPropertiesDetailsBag data={listing} />
              </Stack>
            </Collapse>
          </Box>
        </Card>
      </>
    );
  }
);

export default ListingsMatchRow;
